import React, { useState } from 'react';
import { useApp } from '../../context/AppContext.tsx';
import { HomepageSectionConfig, SiteSettings } from '../../types.ts';
import {
  X,
  CheckCircle2,
  Edit3,
  LayoutDashboard,
  Eye,
  EyeOff,
  BarChart2,
  Save,
  Users,
  Award,
  ShieldCheck,
  HeartHandshake,
  Zap,
  TrendingUp,
  Headphones,
  Star,
  Globe,
  Info,
  HelpCircle,
  Briefcase,
  ArrowRight,
} from 'lucide-react';

interface DirectSectionEditModalProps {
  section: HomepageSectionConfig;
  isOpen: boolean;
  onClose: () => void;
  onNavigateToDashboard: (tabId: string) => void;
}

const iconOptions = [
  { value: 'Users', label: 'Users (Pengguna/Klien)' },
  { value: 'Award', label: 'Award (Penghargaan/Prestasi)' },
  { value: 'ShieldCheck', label: 'ShieldCheck (Keamanan/BNSP)' },
  { value: 'HeartHandshake', label: 'HeartHandshake (Pengalaman)' },
  { value: 'Zap', label: 'Zap (Kecepatan)' },
  { value: 'Star', label: 'Star (Kreatif & Unggulan)' },
  { value: 'TrendingUp', label: 'TrendingUp (Pertumbuhan)' },
  { value: 'Headphones', label: 'Headphones (Layanan CS)' },
  { value: 'Star', label: 'Star (Rating Bintang)' },
];

export default function DirectSectionEditModal({
  section,
  isOpen,
  onClose,
  onNavigateToDashboard,
}: DirectSectionEditModalProps) {
  const { settings, updateSettingsOnServer, showToast, navigate, portfolio } = useApp();

  const [badge, setBadge] = useState(section.badge || '');
  const [title, setTitle] = useState(section.title || '');
  const [subtitle, setSubtitle] = useState(section.subtitle || '');
  const [buttonText, setButtonText] = useState(section.buttonText || '');
  const [buttonEnabled, setButtonEnabled] = useState(section.buttonEnabled ?? true);
  const [enabled, setEnabled] = useState(section.enabled ?? true);
  const [order, setOrder] = useState(section.order || 1);
  const [selectedPortfolioId, setSelectedPortfolioId] = useState(section.selectedPortfolioId || '');

  // Quick Stats Items State (Default if empty)
  const defaultQuickStats = [
    { id: 'qs-1', icon: 'Users', value: '500+', label: 'Klien Korporasi & Instansi', desc: 'Dipercaya berbagai BUMN & swasta' },
    { id: 'qs-2', icon: 'Award', value: '99.8%', label: 'Tingkat Kepuasan Peserta', desc: 'Rating bintang 5 dari ratusan ulasan' },
    { id: 'qs-3', icon: 'ShieldCheck', value: '100%', label: 'Standar Keamanan Bersertifikat', desc: 'Fasilitator & instruktur berlisensi BNSP' },
    { id: 'qs-4', icon: 'HeartHandshake', value: '10+ Thn', label: 'Pengalaman Teruji', desc: 'Ahli dalam event, web & ekosistem UMKM' },
  ];

  const [quickStatsItems, setQuickStatsItems] = useState(
    settings?.quickStats?.items && settings.quickStats.items.length > 0
      ? settings.quickStats.items
      : defaultQuickStats
  );

  // Testimonials Display Mode & Client Logos State
  const defaultClientLogos = [
    { id: 'logo-1', name: 'PT Nusantara Energi', category: 'BUMN & Energi', logoUrl: 'https://images.unsplash.com/photo-1599305445671-ac291c95aaa9?auto=format&fit=crop&w=300&q=80' },
    { id: 'logo-2', name: 'Bank Mandiri Corporate', category: 'Perbankan & Keuangan', logoUrl: 'https://images.unsplash.com/photo-1618005182384-a83a8bd57fbe?auto=format&fit=crop&w=300&q=80' },
    { id: 'logo-3', name: 'Telkom Indonesia Group', category: 'Telekomunikasi', logoUrl: 'https://images.unsplash.com/photo-1516876437184-593fda40c7ce?auto=format&fit=crop&w=300&q=80' },
    { id: 'logo-4', name: 'Pertamina Patra Niaga', category: 'BUMN Migas', logoUrl: 'https://images.unsplash.com/photo-1542744094-3a31b272c490?auto=format&fit=crop&w=300&q=80' },
    { id: 'logo-5', name: 'Kuliner Rempah Nusantara', category: 'F&B & Ekspor UMKM', logoUrl: 'https://images.unsplash.com/photo-1556761175-5973dc0f32e7?auto=format&fit=crop&w=300&q=80' },
    { id: 'logo-6', name: 'Dinas Pariwisata & Kebudayaan', category: 'Instansi Pemerintah', logoUrl: 'https://images.unsplash.com/photo-1572021335469-31706a17aaef?auto=format&fit=crop&w=300&q=80' },
  ];

  const [testimonialDisplayMode, setTestimonialDisplayMode] = useState<'reviews' | 'logos' | 'both'>(
    settings?.testimonialsConfig?.displayMode || 'both'
  );
  const [clientLogosItems, setClientLogosItems] = useState(
    settings?.testimonialsConfig?.clientLogos?.length
      ? settings.testimonialsConfig.clientLogos
      : defaultClientLogos
  );

  const handleAddClientLogo = () => {
    const newLogo = {
      id: `logo-${Date.now()}`,
      name: 'Logo Perusahaan Baru',
      category: 'Klien Korporasi',
      logoUrl: 'https://images.unsplash.com/photo-1560179707-f14e90ef3623?auto=format&fit=crop&w=300&q=80',
    };
    setClientLogosItems([...clientLogosItems, newLogo]);
  };

  const handleUpdateLogoField = (idx: number, field: string, val: string) => {
    const updated = [...clientLogosItems];
    updated[idx] = { ...updated[idx], [field]: val };
    setClientLogosItems(updated);
  };

  const handleDeleteLogo = (idx: number) => {
    setClientLogosItems(clientLogosItems.filter((_, i) => i !== idx));
  };

  const [isSaving, setIsSaving] = useState(false);
  const [saveStatus, setSaveStatus] = useState<{ show: boolean; time: string; msg: string } | null>(null);

  if (!isOpen || !settings) return null;

  // Map section type to dashboard tab
  const getDashboardTab = (type: string): { tabId: string; label: string } => {
    switch (type) {
      case 'hero_headline':
        return { tabId: 'why_choose_us', label: 'Headline Foto/Video & Teks Utama' };
      case 'why_choose_us':
        return { tabId: 'why_choose_us', label: 'Keunggulan & Mengapa Pilih Kami' };
      case 'quick_stats':
        return { tabId: 'frontend_customizer', label: 'Kelola Teks Front-End & Statistik' };
      case 'featured_products':
        return { tabId: 'products', label: 'Kelola Katalog Produk & Layanan' };
      case 'articles_blog_grid':
        return { tabId: 'articles', label: 'Kelola Naskah Artikel & Berita' };
      case 'portfolio_grid':
        return { tabId: 'portfolio', label: 'Kelola Portofolio & Proyek' };
      case 'gallery_video':
        return { tabId: 'gallery', label: 'Kelola Galeri Foto & Youtube' };
      case 'testimonials':
        return { tabId: 'why_choose_us', label: 'Kelola Testimoni Klien' };
      case 'softselling_cta':
        return { tabId: 'frontend_customizer', label: 'Kelola Teks Tombol Kontak WA' };
      case 'personnel_team':
        return { tabId: 'sections', label: 'Kelola Daftar Personel & Tim' };
      case 'media_showcase':
        return { tabId: 'sections', label: 'Kelola Grid Media & Foto Showcase' };
      default:
        return { tabId: 'sections', label: 'Tata Letak Grid Beranda' };
    }
  };

  const dashInfo = getDashboardTab(section.type);

  const handleUpdateStatField = (idx: number, field: string, val: string) => {
    const updated = [...quickStatsItems];
    updated[idx] = { ...updated[idx], [field]: val };
    setQuickStatsItems(updated);
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      const updatedSections = settings.sections.map((sec) => {
        if (sec.id === section.id) {
          return {
            ...sec,
            badge: badge.trim(),
            title: title.trim(),
            subtitle: subtitle.trim(),
            buttonText: buttonText.trim(),
            buttonEnabled,
            enabled,
            order: Number(order),
            selectedPortfolioId: selectedPortfolioId || undefined,
          };
        }
        return sec;
      });

      const payload: Partial<SiteSettings> = {
        sections: updatedSections,
      };

      if (section.type === 'quick_stats') {
        payload.quickStats = {
          items: quickStatsItems,
        };
      }

      if (section.type === 'testimonials') {
        payload.testimonialsConfig = {
          displayMode: testimonialDisplayMode,
          badge: badge.trim(),
          title: title.trim(),
          subtitle: subtitle.trim(),
          clientLogos: clientLogosItems,
        };
      }

      const success = await updateSettingsOnServer(payload);
      if (success) {
        const timeStr = new Date().toLocaleTimeString('id-ID', {
          hour: '2-digit',
          minute: '2-digit',
          second: '2-digit',
        });
        setSaveStatus({
          show: true,
          time: timeStr,
          msg:
            section.type === 'quick_stats'
              ? 'Seluruh data angka & teks statistik berhasil diperbarui!'
              : 'Perubahan judul & teks kolom berhasil disimpan! Beranda langsung diperbarui.',
        });
        showToast('Kolom beranda berhasil diperbarui!', 'success');
      }
    } catch (err) {
      showToast('Gagal menyimpan perubahan kolom', 'error');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-slate-950/75 backdrop-blur-xs flex items-center justify-center p-3 sm:p-5 animate-in fade-in duration-200">
      <div className="bg-white rounded-3xl shadow-2xl border border-slate-200 w-full max-w-2xl overflow-hidden flex flex-col max-h-[90vh] animate-in zoom-in-95 duration-200">
        
        {/* Header */}
        <div className="bg-slate-900 text-white p-4 sm:p-5 flex items-center justify-between border-b border-slate-800">
          <div className="flex items-center gap-3">
            <div className="p-2.5 rounded-2xl bg-blue-500/20 text-blue-400 border border-blue-500/30">
              <Edit3 className="w-5 h-5" />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <span className="text-[10px] font-black uppercase px-2 py-0.5 rounded bg-blue-500/20 text-blue-300 border border-blue-500/30">
                  Edit Langsung Beranda
                </span>
                <span className="text-[10px] font-black uppercase px-2 py-0.5 rounded bg-emerald-500/20 text-emerald-300 border border-emerald-500/30">
                  {section.type === 'hero_headline' ? 'SEO Tag: H1' : 'SEO Tag: H2'}
                </span>
              </div>
              <h3 className="font-extrabold text-base sm:text-lg text-white">
                Edit Kolom: {section.title || section.id}
              </h3>
            </div>
          </div>

          <button
            onClick={onClose}
            className="p-2 text-slate-400 hover:text-white hover:bg-slate-800 rounded-xl transition-colors cursor-pointer"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Form Body */}
        <div className="p-4 sm:p-6 space-y-4 overflow-y-auto font-sans text-slate-900 text-sm">
          
          {/* SEO Structure Explainer Banner */}
          <div className="p-3 bg-blue-50 border border-blue-200 rounded-2xl flex items-start gap-2.5 text-xs text-blue-900">
            <Info className="w-4 h-4 text-blue-600 shrink-0 mt-0.5" />
            <div className="space-y-0.5">
              <strong className="block text-blue-950 font-bold">
                Struktur SEO Heading Google: {section.type === 'hero_headline' ? 'Tag H1 (Utama)' : 'Tag H2 (Subjudul Seksi)'}
              </strong>
              <p className="text-blue-800 text-[11px] leading-relaxed">
                {section.type === 'hero_headline'
                  ? 'Judul kolom ini dirender sebagai Tag <H1> utama halaman depan yang sangat diutamakan Googlebot untuk mengindeks kata kunci bisnis Anda.'
                  : 'Judul kolom ini otomatis dirender sebagai Tag <H2> seksi beranda yang memperkuat topik dan memudahkan Google mengindeks sub-layanan website.'}
              </p>
            </div>
          </div>

          {/* Status Visibility */}
          <div className="p-3.5 bg-slate-50 border border-slate-200 rounded-2xl flex items-center justify-between gap-3">
            <div className="flex items-center gap-2.5">
              {enabled ? (
                <Eye className="w-5 h-5 text-emerald-600" />
              ) : (
                <EyeOff className="w-5 h-5 text-slate-400" />
              )}
              <div>
                <p className="font-bold text-slate-900 text-xs sm:text-sm">Tampilkan Kolom Ini di Beranda</p>
                <p className="text-[11px] text-slate-500">Status saat ini: {enabled ? 'Aktif Tayang' : 'Disembunyikan'}</p>
              </div>
            </div>

            <button
              type="button"
              onClick={() => setEnabled(!enabled)}
              className={`px-3.5 py-1.5 rounded-xl font-bold text-xs transition-all cursor-pointer ${
                enabled
                  ? 'bg-slate-900 text-white shadow-xs'
                  : 'bg-slate-200 text-slate-700 hover:bg-slate-300'
              }`}
            >
              {enabled ? '✓ Ditampilkan' : 'Off (Sembunyi)'}
            </button>
          </div>

          {/* Special Quick Stats Items Editor if section.type === 'quick_stats' */}
          {section.type === 'quick_stats' ? (
            <div className="space-y-4 pt-1 border-t border-b border-slate-200 py-4">
              <div className="flex items-center justify-between">
                <h4 className="font-black text-slate-900 text-sm flex items-center gap-2">
                  <BarChart2 className="w-4 h-4 text-blue-600" />
                  <span>Edit Isi 4 Kartu Angka Statistik (Tag SEO: H3)</span>
                </h4>
                <span className="text-[11px] font-semibold text-slate-500">Perubahan langsung tampil di beranda</span>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3.5">
                {quickStatsItems.map((stat, idx) => (
                  <div key={stat.id || idx} className="p-3.5 bg-slate-50 border border-slate-200 rounded-2xl space-y-2.5 shadow-2xs">
                    <div className="flex items-center justify-between gap-2 border-b border-slate-200 pb-1.5">
                      <span className="text-[10px] font-black uppercase text-blue-800 bg-blue-100 px-2 py-0.5 rounded">
                        Kartu #{idx + 1} • SEO H3
                      </span>
                      <select
                        value={stat.icon}
                        onChange={(e) => handleUpdateStatField(idx, 'icon', e.target.value)}
                        className="text-xs font-bold bg-white border border-slate-300 rounded-lg px-2 py-1 focus:ring-1 focus:ring-blue-500 cursor-pointer"
                      >
                        {iconOptions.map((opt) => (
                          <option key={opt.value} value={opt.value}>
                            {opt.label}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div className="space-y-1">
                      <label className="text-[11px] font-bold text-slate-600 block">Angka / Nilai Utama:</label>
                      <input
                        type="text"
                        value={stat.value}
                        onChange={(e) => handleUpdateStatField(idx, 'value', e.target.value)}
                        placeholder="Contoh: 500+, 99.8%, 10+ Thn"
                        className="w-full px-3 py-1.5 bg-white border border-slate-300 rounded-xl font-extrabold text-slate-900 text-xs sm:text-sm focus:ring-2 focus:ring-blue-500 focus:outline-none"
                      />
                    </div>

                    <div className="space-y-1">
                      <label className="text-[11px] font-bold text-slate-600 block">Judul / Label Kartu (SEO H3):</label>
                      <input
                        type="text"
                        value={stat.label}
                        onChange={(e) => handleUpdateStatField(idx, 'label', e.target.value)}
                        placeholder="Contoh: Klien Korporasi & Instansi"
                        className="w-full px-3 py-1.5 bg-white border border-slate-300 rounded-xl font-bold text-slate-800 text-xs focus:ring-2 focus:ring-blue-500 focus:outline-none"
                      />
                    </div>

                    <div className="space-y-1">
                      <label className="text-[11px] font-bold text-slate-600 block">Keterangan / Deskripsi Ringkas:</label>
                      <input
                        type="text"
                        value={stat.desc}
                        onChange={(e) => handleUpdateStatField(idx, 'desc', e.target.value)}
                        placeholder="Contoh: Dipercaya berbagai BUMN & swasta"
                        className="w-full px-3 py-1.5 bg-white border border-slate-300 rounded-xl font-medium text-slate-700 text-xs focus:ring-2 focus:ring-blue-500 focus:outline-none"
                      />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <>
              {/* Badge Label (Hidden for portfolio_grid as single project layout removes top badges) */}
              {section.type !== 'portfolio_grid' && (
                <div className="space-y-1.5">
                  <label className="text-xs font-bold text-slate-700 uppercase tracking-wider flex items-center justify-between">
                    <span>Label Mini / Badge Atas</span>
                    <span className="text-[10px] text-slate-400 font-bold">SEO Eye-Catcher</span>
                  </label>
                  <input
                    type="text"
                    value={badge}
                    onChange={(e) => setBadge(e.target.value)}
                    placeholder="Contoh: PAKET POPULER, WISATA MICE..."
                    className="w-full px-3.5 py-2.5 bg-slate-50 border border-slate-300 rounded-xl font-semibold text-slate-900 focus:bg-white focus:ring-2 focus:ring-blue-500 focus:outline-none transition-all text-xs sm:text-sm"
                  />
                </div>
              )}

              {/* Title with SEO Heading Indicator */}
              <div className="space-y-1.5">
                <label className="text-xs font-black text-slate-900 uppercase tracking-wider flex items-center justify-between">
                  <span className="flex items-center gap-1.5">
                    <span>Judul Utama Grid Project</span>
                    <span className="px-2 py-0.5 bg-blue-100 text-blue-800 text-[10px] font-black rounded">
                      {section.type === 'hero_headline' ? 'Tag SEO: H1' : 'Tag SEO: H2'}
                    </span>
                  </span>
                  <span className="text-[10px] text-slate-400 font-bold">Target Keyword Google</span>
                </label>
                <input
                  type="text"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  placeholder="Ketik Judul Kolom Project..."
                  className="w-full px-3.5 py-2.5 bg-slate-50 border border-slate-300 rounded-xl font-black text-slate-900 focus:bg-white focus:ring-2 focus:ring-blue-500 focus:outline-none transition-all text-sm sm:text-base"
                />
              </div>

              {/* Subtitle with SEO Description Tag */}
              <div className="space-y-1.5">
                <label className="text-xs font-bold text-slate-700 uppercase tracking-wider flex items-center justify-between">
                  <span className="flex items-center gap-1.5">
                    <span>Sub-judul / Ringkasan Singkat Grid</span>
                    <span className="px-2 py-0.5 bg-slate-100 text-slate-700 text-[10px] font-bold rounded">
                      SEO Meta Context
                    </span>
                  </span>
                  <span className="text-[10px] text-slate-400 font-bold">Penjelas Kata Kunci</span>
                </label>
                <textarea
                  rows={3}
                  value={subtitle}
                  onChange={(e) => setSubtitle(e.target.value)}
                  placeholder="Ketik deskripsi atau penjelas ringkas di bawah judul..."
                  className="w-full px-3.5 py-2.5 bg-slate-50 border border-slate-300 rounded-xl font-medium text-slate-800 focus:bg-white focus:ring-2 focus:ring-blue-500 focus:outline-none transition-all text-xs sm:text-sm"
                />
              </div>

              {/* Special Single-Project Banner for Portfolio Grid */}
              {section.type === 'portfolio_grid' && (
                <div className="p-4 bg-emerald-50 border border-emerald-200/80 rounded-2xl space-y-3 text-xs text-emerald-950">
                  <div className="flex items-center gap-2 font-black text-emerald-900">
                    <Briefcase className="w-4 h-4 text-emerald-700" />
                    <span>Pilih Portofolio Proyek yang Ditampilkan di Kotak Grid Ini:</span>
                  </div>
                  <p className="text-emerald-800 text-[11px] leading-relaxed">
                    Pilih salah satu portofolio proyek yang sudah Anda buat di menu Portofolio untuk dimunculkan pada kotak grid beranda ini.
                  </p>

                  {/* Dropdown Selector */}
                  <div className="space-y-1.5 pt-1">
                    <label className="text-[11px] font-bold text-slate-700 block">
                      Daftar Portofolio Proyek Tersedia:
                    </label>
                    {portfolio.length === 0 ? (
                      <div className="p-2.5 bg-amber-50 border border-amber-200 rounded-xl text-amber-800 text-[11px]">
                        Belum ada portofolio proyek. Buat proyek terlebih dahulu di Dashboard Admin.
                      </div>
                    ) : (
                      <select
                        value={selectedPortfolioId || section.selectedPortfolioId || (portfolio[0]?.id || '')}
                        onChange={(e) => setSelectedPortfolioId(e.target.value)}
                        className="w-full px-3 py-2 text-xs bg-white border-2 border-emerald-500 rounded-xl font-bold text-slate-900 focus:outline-none focus:ring-2 focus:ring-emerald-500 shadow-2xs cursor-pointer"
                      >
                        {portfolio.map((p, pIdx) => (
                          <option key={p.id} value={p.id}>
                            Proyek #{pIdx + 1}: {p.title} {p.client ? `(${p.client})` : ''}
                          </option>
                        ))}
                      </select>
                    )}
                  </div>

                  <div className="pt-2 border-t border-emerald-200/60">
                    <button
                      type="button"
                      onClick={() => {
                        onClose();
                        try {
                          localStorage.setItem('admin_target_tab', 'portfolio');
                        } catch (e) {}
                        navigate('admin');
                      }}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-emerald-700 hover:bg-emerald-800 text-white font-bold text-xs shadow-xs transition-colors cursor-pointer"
                    >
                      <span>✏️ Buka Menu Portofolio di Dashboard Admin</span>
                      <ArrowRight className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </div>
              )}

              {/* CTA Button Settings for Blog, Products, Gallery, Media Showcase (Portfolio Grid excluded as "View All" is removed) */}
              {['articles_blog_grid', 'featured_products', 'gallery_video', 'media_showcase'].includes(section.type) && (
                <div className="p-3.5 bg-slate-100/90 rounded-2xl border border-slate-200 space-y-3">
                  <div className="flex items-center justify-between">
                    <div>
                      <span className="text-xs font-bold text-slate-900 block">Tombol Tautan / CTA Menuju Halaman Terkait</span>
                      <span className="text-[11px] text-slate-500">Atur teks tombol klik pembaca di grid beranda</span>
                    </div>
                    <button
                      type="button"
                      onClick={() => setButtonEnabled(!buttonEnabled)}
                      className={`px-3 py-1 rounded-lg text-xs font-bold transition-all ${
                        buttonEnabled
                          ? 'bg-emerald-600 text-white shadow-xs'
                          : 'bg-slate-300 text-slate-700'
                      }`}
                    >
                      {buttonEnabled ? '✓ Tombol Aktif' : 'Off'}
                    </button>
                  </div>

                  {buttonEnabled && (
                    <div>
                      <label className="text-xs font-semibold text-slate-700 block mb-1">
                        Tulisan Tombol Klik:
                      </label>
                      <input
                        type="text"
                        value={buttonText}
                        onChange={(e) => setButtonText(e.target.value)}
                        placeholder="Contoh: Buka Semua Artikel & Berita →"
                        className="w-full px-3 py-2 bg-white border border-slate-300 rounded-xl font-medium text-slate-900 text-xs sm:text-sm focus:ring-2 focus:ring-blue-500 focus:outline-none"
                      />
                    </div>
                  )}
                </div>
              )}

              {/* Special Controls for Testimonials & Client Logos */}
              {section.type === 'testimonials' && (
                <div className="mt-4 p-4 bg-slate-100/90 rounded-2xl border border-slate-300/80 space-y-4">
                  <div>
                    <label className="text-xs font-bold text-slate-900 uppercase tracking-wider block mb-2">
                      Pilihan Tampilan Testimoni / Logo Klien:
                    </label>
                    <div className="grid grid-cols-3 gap-2">
                      <button
                        type="button"
                        onClick={() => setTestimonialDisplayMode('both')}
                        className={`p-2.5 rounded-xl border text-xs font-bold transition-all text-center cursor-pointer ${
                          testimonialDisplayMode === 'both'
                            ? 'bg-slate-900 text-white border-slate-900 shadow-xs'
                            : 'bg-white text-slate-700 border-slate-300 hover:bg-slate-50'
                        }`}
                      >
                        💬 + 🏢 Keduanya
                      </button>
                      <button
                        type="button"
                        onClick={() => setTestimonialDisplayMode('reviews')}
                        className={`p-2.5 rounded-xl border text-xs font-bold transition-all text-center cursor-pointer ${
                          testimonialDisplayMode === 'reviews'
                            ? 'bg-slate-900 text-white border-slate-900 shadow-xs'
                            : 'bg-white text-slate-700 border-slate-300 hover:bg-slate-50'
                        }`}
                      >
                        💬 Hanya Kartu Ulasan
                      </button>
                      <button
                        type="button"
                        onClick={() => setTestimonialDisplayMode('logos')}
                        className={`p-2.5 rounded-xl border text-xs font-bold transition-all text-center cursor-pointer ${
                          testimonialDisplayMode === 'logos'
                            ? 'bg-slate-900 text-white border-slate-900 shadow-xs'
                            : 'bg-white text-slate-700 border-slate-300 hover:bg-slate-50'
                        }`}
                      >
                        🏢 Hanya Logo Klien (Carousel)
                      </button>
                    </div>
                  </div>

                  {/* Client Logos List */}
                  <div className="space-y-3 pt-3 border-t border-slate-200">
                    <div className="flex items-center justify-between">
                      <span className="text-xs font-bold text-slate-800">
                        Daftar Logo Klien & Mitra BUMN ({clientLogosItems.length})
                      </span>
                      <button
                        type="button"
                        onClick={handleAddClientLogo}
                        className="text-xs font-bold text-blue-700 hover:text-blue-900 bg-white border border-slate-300 px-3 py-1 rounded-lg transition"
                      >
                        + Tambah Logo Klien
                      </button>
                    </div>

                    <div className="max-h-48 overflow-y-auto space-y-2 pr-1">
                      {clientLogosItems.map((logo, idx) => (
                        <div key={logo.id || idx} className="p-2.5 bg-white rounded-xl border border-slate-200 flex items-center gap-3">
                          <img src={logo.logoUrl} alt={logo.name} className="w-10 h-10 object-cover rounded-lg bg-slate-50 border border-slate-100 shrink-0" />
                          <div className="flex-1 min-w-0 space-y-1">
                            <input
                              type="text"
                              value={logo.name}
                              onChange={(e) => handleUpdateLogoField(idx, 'name', e.target.value)}
                              placeholder="Nama Instansi/Klien"
                              className="w-full text-xs font-bold bg-slate-50 px-2 py-1 rounded border border-slate-200 focus:bg-white focus:outline-none"
                            />
                            <input
                              type="text"
                              value={logo.logoUrl}
                              onChange={(e) => handleUpdateLogoField(idx, 'logoUrl', e.target.value)}
                              placeholder="URL Logo Gambar"
                              className="w-full text-[11px] text-slate-500 bg-slate-50 px-2 py-0.5 rounded border border-slate-200 focus:bg-white focus:outline-none"
                            />
                          </div>
                          <button
                            type="button"
                            onClick={() => handleDeleteLogo(idx)}
                            className="p-1.5 text-rose-500 hover:bg-rose-50 rounded-lg transition"
                            title="Hapus Logo"
                          >
                            <X className="w-4 h-4" />
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </>
          )}

          {/* Save Status Notification */}
          {saveStatus?.show && (
            <div className="p-3 bg-emerald-50 border border-emerald-200 rounded-2xl flex items-center justify-between text-xs text-emerald-800 animate-in fade-in">
              <div className="flex items-center gap-2">
                <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0" />
                <span>{saveStatus.msg}</span>
              </div>
              <span className="text-[10px] text-emerald-600 font-bold">{saveStatus.time}</span>
            </div>
          )}
        </div>

        {/* Modal Footer */}
        <div className="bg-slate-50 border-t border-slate-200 p-4 sm:p-5 flex flex-wrap items-center justify-between gap-3">
          <button
            type="button"
            onClick={() => {
              onClose();
              onNavigateToDashboard(dashInfo.tabId);
            }}
            className="text-xs font-bold text-slate-700 hover:text-slate-950 flex items-center gap-1.5 px-3 py-2 rounded-xl hover:bg-slate-200/70 transition"
          >
            <LayoutDashboard className="w-4 h-4 text-slate-500" />
            <span>Buka Dashboard Penuh ({dashInfo.label})</span>
          </button>

          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 rounded-xl text-xs font-bold text-slate-600 hover:bg-slate-200/70 transition"
            >
              Tutup
            </button>

            <button
              type="button"
              onClick={handleSave}
              disabled={isSaving}
              className="px-5 py-2 rounded-xl text-xs font-bold text-white bg-slate-900 hover:bg-slate-800 active:scale-98 transition flex items-center gap-1.5 shadow-md cursor-pointer"
            >
              <Save className="w-4 h-4" />
              <span>{isSaving ? 'Menyimpan...' : 'Simpan Perubahan'}</span>
            </button>
          </div>
        </div>

      </div>
    </div>
  );
}
